import {
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AvailabilityService } from '../../../shared/services/availability.service';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ContainerComponent } from '../../../shared/container/container.component';
import { ServiceDemandControllerService } from '../../../core/generated/api/serviceDemandController.service';
import { ProfileProviderPJ, ServiceDemand } from '../../../core/generated';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-service-demand',
    standalone: true,
    imports: [CommonModule, MaterialModule, RouterLink, EnumDescriptionPipe, AvailabilityComponent, ContainerComponent],
    templateUrl: './service-demand.component.html',
    styleUrl: './service-demand.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceDemandComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly serviceDemandService = inject(ServiceDemandControllerService);
  private readonly profilePJContext = inject(ProviderPJContextService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly availabilityService = inject(AvailabilityService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  serviceDemandList: ServiceDemand[] = [];
  providerPJ: ProfileProviderPJ | undefined;
  isLoading = false;

  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);

  private currentCnpj: string | undefined;

  ngOnInit(): void {
    this.setupContextSubscription();
    this.initialize();
  }

  private setupContextSubscription(): void {
    this.profilePJContext.providerPJ$.pipe(
      filter((providerPJ): providerPJ is ProfileProviderPJ => providerPJ !== null),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((providerPJ) => {
      this.handleProviderPJChange(providerPJ);
    });
  }

  private handleProviderPJChange(providerPJ: ProfileProviderPJ): void {
    const hasChanged = !this.providerPJ || this.providerPJ.id !== providerPJ.id;
    
    if (hasChanged) {
      this.providerPJ = providerPJ;
      this.serviceDemandList = [];
      
      if (providerPJ.cnpj) {
        this.loadServiceDemands();
      }
      
      this.cdr.markForCheck();
    }
  }

  async initialize(): Promise<void> {
    await this.profilePJContext.ensureContextFromUrlIfMissing();

    const context = this.profilePJContext.getProviderPJ();

    if (context) {
      this.providerPJ = context;

      if (context.cnpj && this.serviceDemandList.length === 0) {
        this.loadServiceDemands();
      }
    } else {
      this.redirectToProvider();
    }
  }

  private redirectToProvider(): void {
    const providerPJId = this.route.parent?.snapshot.paramMap.get('id');
    if (providerPJId) {
      this.router.navigate([`/providerPJ/${providerPJId}`]);
    }
  }

  loadServiceDemands(): void {
    if (!this.providerPJ?.cnpj) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceDemandService
      .findByCnpj1(this.providerPJ.cnpj)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.serviceDemandList = response || [];
          this.currentCnpj = this.providerPJ?.cnpj;

          if (this.serviceDemandList.length === 0) {
            this.navigateToServiceDemandForm();
          }
          
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading service demands:', error);
          this.serviceDemandList = [];
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private navigateToServiceDemandForm(): void {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    if (id) {
      this.router.navigate([`providerPJ/${id}/serviceDemand/serviceDemandForm`]);
    }
  }

  trackById(index: number, item: ServiceDemand): string | number {
    return item?.id ?? index;
  }

  getAvailabilityDescription(availability: number[]): string[] {
    return this.availabilityService.getAvailabilityDescription(availability);
  }

  publish(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceDemandService
      .publish1(Number(id), { publicationDate: '' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Limpar o cache para forçar reload
          this.currentCnpj = undefined;
          this.isLoading = false;
          // Recarregar a lista
          this.loadServiceDemands();
        },
        error: (error) => {
          console.error('Error publishing service demand:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  deleteServiceDemand(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceDemandService
      .deleteById2(Number(id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Limpar o cache para forçar reload
          this.currentCnpj = undefined;
          this.isLoading = false;
          // Recarregar a lista
          this.loadServiceDemands();
        },
        error: (error) => {
          console.error('Error deleting service demand:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }
}
